import type { ComponentType } from "react";
import type { ConfirmData, Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateTranslationMutation,
  UpdateTranslationMutation,
  TranslationEditQuery,
  TranslationShowQuery,
  type UpdateTranslationInput,
} from "@/admin/entities/translations";
import type {
  TranslationEditQueryType,
  TranslationShowQueryType,
} from "@/types/graphql/admin.generated";

export interface VersionOption {
  label: string;
  value: string;
}

export const filterSchema: Schema = {
  project: {
    label: "Проект",
    type: "select",
    priority: "quick",
    xs: 12,
    md: 4,
    fullWidth: true,
    options: [
      { label: "Все", value: "" },
      { label: "site", value: "site" },
      { label: "cabinet", value: "cabinet" },
    ],
  },
  namespace: {
    label: "Неймспейс",
    helperText: "Точное совпадение (регистр не важен)",
    type: "text",
    priority: "primary",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
  version: {
    label: "Версия",
    helperText: "Точное совпадение (регистр не важен)",
    type: "text",
    priority: "primary",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
  withDeleted: {
    label: "Показывать удалённые",
    type: "checkbox",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  deleted: {
    label: "Статус",
    type: "select",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
    options: [
      { label: "Все активные и удалённые", value: "" },
      { label: "Только активные", value: "false" },
      { label: "Только удалённые", value: "true" },
    ],
  },
};

/**
 * `namespace`/`version` в модели хранятся в нижнем регистре, а бэкенд
 * фильтрует строгим равенством — без нормализации ввод «Partner» молча даёт
 * пустой список. Приводим к тому же виду, что и модель, только для запроса.
 */
export function normalizeTranslationsFilter(
  filter: Record<string, unknown>
): Record<string, unknown> {
  const lowercasedFields = new Set(["namespace", "version"]);

  return Object.entries(filter).reduce<Record<string, unknown>>(
    (normalized, [key, value]) => {
      if (!lowercasedFields.has(key) || typeof value !== "string") {
        normalized[key] = value;
        return normalized;
      }

      const trimmed = value.trim().toLowerCase();

      // пустая строка — как отсутствие фильтра (см. cleanFilter в CrudList)
      if (trimmed) normalized[key] = trimmed;

      return normalized;
    },
    {}
  );
}

export interface MainFieldsOptions {
  /**
   * Режим редактирования: `fromVersionId` тут не «настройка», а разрушительная
   * операция — заменяет ВСЕ ключи во всех языках копией выбранной версии.
   */
  destructiveCopy?: boolean;
}

export function getMainFieldsSchema(
  versionOptions: VersionOption[],
  { destructiveCopy = false }: MainFieldsOptions = {}
): Schema {
  return {
    project: {
      required: true,
      label: "Проект",
      type: "select",
      options: [
        { label: "site", value: "site" },
        { label: "cabinet", value: "cabinet" },
      ],
      xs: 12,
      md: 6,
      fullWidth: true,
    },
    namespace: {
      required: true,
      label: "Неймспейс",
      helperText: "site → translation, cabinet → partner",
      type: "text",
      xs: 12,
      md: 6,
      fullWidth: true,
    },
    version: {
      required: true,
      label: "Версия",
      helperText: "develop, latest или произвольная (feature-x)",
      type: "text",
      xs: 12,
      md: 6,
      fullWidth: true,
    },
    fromVersionId: {
      label: "Скопировать строки из версии",
      helperText: destructiveCopy
        ? "Осторожно: заменит ВСЕ ключи во всех языках этого перевода копией выбранной версии"
        : undefined,
      type: "select",
      options: [{ label: "— пусто —", value: "" }, ...versionOptions],
      xs: 12,
      md: 6,
      fullWidth: true,
    },
  };
}

export function getCreateSchema(
  versionOptions: VersionOption[]
): CreateSchemaDefinition {
  return {
    main: {
      label: "Новый перевод",
      createMutation: CreateTranslationMutation,
      schema: getMainFieldsSchema(versionOptions),
    },
  };
}

/**
 * Базовая edit-схема без showComponent — feature-model не может напрямую
 * импортировать components/ (boundaries/entry-point). Конкретный
 * TranslationShowComponent прокидывается через createEditSchema() из ui-слоя,
 * который получает его через публичный API фичи (см. Configs/Languages).
 */
export function getEditSchemaBase(
  versionOptions: VersionOption[]
): EditSchemaDefinition {
  return {
    details: {
      label: "Просмотр",
      type: "show" as const,
      getTitle: (data: unknown) => {
        const typed = data as TranslationShowQueryType;
        const t = typed?.translation;
        return t ? `${t.project}/${t.namespace}/${t.version}` : null;
      },
      showQuery: TranslationShowQuery,
    },
    edit: {
      label: "Настройки",
      type: "form" as const,
      updateMutation: UpdateTranslationMutation,
      editQuery: TranslationEditQuery,
      // Схема зависит от редактируемой записи: копировать строки «из самого
      // себя» бессмысленно, поэтому текущий перевод из опций исключаем.
      schema: (data: unknown): Schema => {
        const { translation } = (data ??
          {}) as Partial<TranslationEditQueryType>;

        return getMainFieldsSchema(
          translation
            ? versionOptions.filter((o) => o.value !== translation.id)
            : versionOptions,
          { destructiveCopy: true }
        );
      },
      // Непустой fromVersionId в update — не «настройка», а полная замена
      // всех вариантов и всех ключей (TranslationService.update).
      getConfirm: ({ values }): ConfirmData | null =>
        values.fromVersionId
          ? {
              confirmTitle: "Заменить все строки перевода?",
              confirmText:
                "Выбрана версия в поле «Скопировать строки из версии». " +
                "ВСЕ ключи во ВСЕХ языках этого перевода будут заменены копией выбранной версии. " +
                "Текущие значения будут потеряны без возможности отмены.",
            }
          : null,
      getEditData: (data: unknown): UpdateTranslationInput => {
        const { translation } = data as TranslationEditQueryType;
        return {
          project: translation.project,
          namespace: translation.namespace,
          version: translation.version,
        };
      },
    },
  };
}

export function getEditSchema(
  versionOptions: VersionOption[],
  showComponent: ComponentType<{ editData?: unknown; id?: string }>
): EditSchemaDefinition {
  const base = getEditSchemaBase(versionOptions);
  return {
    ...base,
    details: {
      ...base.details,
      showComponent,
    },
  };
}
